<template>
  <div class="stock-check-page">
    <!-- 头部 -->
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">盘点单</h1>
      <el-button type="primary" size="small" @click="handleAddCheck">新增</el-button>
    </header>

    <!-- 主内容 -->
    <main class="content">
      <!-- 筛选栏 -->
      <div class="filter-bar">
        <el-button
          v-for="opt in statusOptions"
          :key="opt.value"
          :type="checkStatusFilter === opt.value ? 'primary' : ''"
          size="small"
          @click="checkStatusFilter = opt.value; onFilterChange()"
        >{{ opt.label }}</el-button>
      </div>

      <!-- 搜索 -->
      <el-input v-model="listCombinedSearchText" placeholder="项目组 供应商（空格分割）" clearable @clear="clearListSearch" class="search-bar" />

      <!-- 提示 -->
      <el-alert v-if="statusMsg" :title="statusMsg" :type="statusType === 'success' ? 'success' : statusType === 'error' || statusType === 'danger' ? 'error' : 'info'" show-icon :closable="false" class="status-alert" />

      <!-- 加载 -->
      <div v-if="loading" v-loading="true" class="loading-area" />

      <!-- 错误 -->
      <el-alert v-if="errorMsg" :title="errorMsg" type="error" show-icon :closable="false" class="status-alert" />

      <!-- 空状态 -->
      <el-empty v-if="checkList.length === 0 && !loading && !errorMsg" description="暂无盘点订单">
        <el-button type="primary" @click="handleAddCheck">立即创建</el-button>
      </el-empty>

      <!-- 卡片列表 -->
      <div v-else-if="!loading && !errorMsg" class="card-list">
        <div v-for="item in checkList" :key="item.id" class="stock-card" @click="navigateToDetail(item.id, item.bizDept)">
          <div class="card-top">
            <span class="card-no">{{ item.inventeNo }}</span>
            <el-tag :type="getTagType(item.checkStatus)" size="small" effect="dark" round>{{ getStatusText(item.checkStatus) }}</el-tag>
          </div>
          <div class="card-info">
            <div class="info-row">
              <span class="info-label">创建时间</span>
              <span class="info-value">{{ formatDate(item.createTime) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">项目组</span>
              <span class="info-value">{{ item.deptName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">供应商</span>
              <span class="info-value supplier-link" :class="{ clickable: !!item.supplierName }" @click.stop="openSupplierNameModal(item)">{{ item.supplierName || '-' }}</span>
            </div>
            <div v-if="item.checkStatus === 2" class="info-row">
              <span class="info-label">不通过原因</span>
              <span class="info-value text-danger">{{ item.reason }}</span>
            </div>
          </div>
          <div class="card-actions" @click.stop>
            <el-button type="primary" size="small" @click="handleEnterCheck(item.id, item.bizDept)" :disabled="item.status === 2 || item.status === 3">
              {{ item.checkStatus === 0 ? '开始盘点' : item.checkStatus === 2 ? '审核未通过' : '审核通过' }}
            </el-button>
            <el-button size="small" type="danger" plain :loading="deletingId === item.id" @click="handleDeleteCheck(item.id)">
              {{ deletingId === item.id ? '' : '删除' }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="total > 0 && !loading && !errorMsg" class="pagination-wrap">
        <el-pagination background layout="prev, pager, next" :total="total" :page-size="size" :current-page="current" @current-change="handlePageChange" size="small" />
      </div>
    </main>

    <!-- 新增盘点弹窗 -->
    <el-dialog v-model="showAddModal" title="新增项目组盘点单" width="95%" :close-on-click-modal="false" top="8vh">
      <el-alert v-if="statusMsg" :title="statusMsg" :type="statusType === 'success' ? 'success' : statusType === 'error' || statusType === 'danger' ? 'error' : 'info'" show-icon :closable="false" class="status-alert" />

      <!-- 项目组选择 -->
      <el-form-item label="选择项目组" class="form-item">
        <el-select v-model="selectedMaterialGroup" filterable remote :remote-method="handleFilterMaterialGroups" :loading="loading" placeholder="输入项目组名称搜索" value-key="id" @change="handleSelectGroup" @visible-change="(v) => { if (!v) showMaterialGroupDropdown = false }">
          <el-option v-for="group in filteredMaterialGroups" :key="group.id" :label="group.name" :value="group.id" />
        </el-select>
      </el-form-item>

      <!-- 添加模式切换 -->
      <el-radio-group v-model="currentMode" class="mode-switch">
        <el-radio-button value="field">现场添加</el-radio-button>
        <el-radio-button value="normal">常规添加</el-radio-button>
        <el-radio-button value="materialNo">编码添加</el-radio-button>
      </el-radio-group>

      <!-- 现场模式提示 -->
      <el-alert v-if="currentMode === 'field'" title="现场模式：可直接创建盘点单，盘点时再添加具体物料" type="info" show-icon :closable="false" class="status-alert" />

      <!-- 仓库选择 -->
      <el-form-item label="选择仓库" class="form-item">
        <el-radio-group v-if="stores.length > 0" v-model="selectedStoreId" class="store-radio-group">
          <el-radio v-for="store in stores" :key="store.id" :value="store.id" border class="store-radio">{{ store.wareName }}</el-radio>
        </el-radio-group>
        <el-empty v-else-if="!storeLoading && selectedMaterialGroup" description="暂无仓库" :image-size="40" />
        <el-input v-if="storeLoading" :model-value="''" disabled placeholder="加载中..." />
      </el-form-item>

      <!-- 编码添加 - 物料编码输入 -->
      <template v-if="currentMode === 'materialNo'">
        <el-form-item label="输入物料编码" class="form-item">
          <el-input v-model="materialNoInput" placeholder="输入物料编码" clearable />
        </el-form-item>
        <div v-if="materialNoCandidates.length > 0" class="selectable-list">
          <div v-for="material in materialNoCandidates" :key="material.id" class="selectable-item" :class="{ active: selectedMaterialNo === material.materialNo }" @click="selectMaterialNo(material.materialNo)">
            <div class="item-main">
              <span class="item-no">{{ material.materialNo }}</span>
              <span class="item-sub">{{ material.materialName }}</span>
            </div>
            <span v-if="selectedMaterialNo === material.materialNo" class="check-mark">✓</span>
          </div>
        </div>
        <p class="form-hint">输入物料编码后从列表选择一条</p>
      </template>

      <!-- 常规模式 - 供应商选择 -->
      <template v-if="currentMode === 'normal'">
        <el-form-item label="选择供应商" class="form-item">
          <el-select
            v-model="selectedSupplierId"
            filterable
            remote
            reserve-keyword
            placeholder="输入供应商名称搜索"
            :remote-method="handleSupplierSearch"
            :loading="supplierLoading"
            value-key="id"
            @change="handleSupplierChange"
          >
            <el-option
              v-for="item in suppliers"
              :key="item.id"
              :label="item.supplierName"
              :value="item.id"
            >
              <span>{{ item.supplierName }}</span>
              <span style="float: right; color: #8492a6; font-size: 15px">{{ item.supplierCode }}</span>
            </el-option>
          </el-select>
        </el-form-item>

        <!-- 物料选择 -->
        <el-form-item label="选择物料编码" class="form-item">
          <el-input v-model="materialSearchText" placeholder="输入物料编码/名称搜索" clearable />
        </el-form-item>
        <div v-if="materials.length > 0" class="selectable-list">
          <div v-for="material in materials" :key="material.id" class="selectable-item" :class="{ active: selectedMaterials.includes(material.id) }" @click="toggleMaterialSelection(material.id)">
            <div class="item-main">
              <span class="item-no">{{ material.materialNo }}</span>
              <span class="item-sub">{{ material.materialName }}</span>
            </div>
            <span v-if="selectedMaterials.includes(material.id)" class="check-mark">✓</span>
          </div>
        </div>
        <el-empty v-else-if="!materialLoading && selectedSupplierId" description="暂无匹配的物料" :image-size="40" />
        <p class="form-hint">点击选择物料编码，可多选</p>
      </template>

      <template #footer>
        <el-button @click="resetModal">取消</el-button>
        <el-button type="primary" :loading="createLoading" :disabled="!canCreate" @click="createCheckOrder">
          {{ createLoading ? '创建中...' : '创建盘点单' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 供应商名称弹窗 -->
    <el-dialog v-model="showSupplierNameModal" :title="supplierNameModalTitle" width="85%" :close-on-click-modal="false">
      <el-empty v-if="supplierNameModalItems.length === 0" description="暂无供应商" :image-size="40" />
      <div v-else class="supplier-list-popup">
        <el-tag v-for="(name, idx) in supplierNameModalItems" :key="`${idx}-${name}`" class="supplier-tag">{{ name }}</el-tag>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import * as stockCheckApi from "@/api/stockCheck";
import { getProjectTeams, getSuppliersByGroupId, getMaterialsBySupplierId } from "@/api/projectTeamStrockCheck";

export default {
  name: "ProjectTeamStockCheck",
  setup() {
    const router = useRouter();

    // --- 数据 ---
    const checkList = ref([]);
    const loading = ref(false);
    const errorMsg = ref("");
    const statusMsg = ref("");
    const statusType = ref("success");
    const showAddModal = ref(false);
    const currentMode = ref("field");
    const createLoading = ref(false);
    const current = ref(1);
    const size = ref(10);
    const total = ref(0);
    const checkStatusFilter = ref(0);
    const listCombinedSearchText = ref("");
    const listSearchDeptName = ref("");
    const listSearchSupplierName = ref("");
    let listSearchTimer = null;

    const showSupplierNameModal = ref(false);
    const supplierNameModalTitle = ref("供应商");
    const supplierNameModalItems = ref([]);
    const deletingId = ref("");

    const statusOptions = [
      { label: "待盘点", value: 0 },
      { label: "通过", value: 1 },
      { label: "不通过", value: 2 },
    ];

    const totalPages = computed(() => Math.ceil((total.value || 0) / (size.value || 1)) || 1);

    // 项目组
    const selectedMaterialGroup = ref("");
    const selectedMaterialGroupName = ref("");
    const filteredMaterialGroups = ref([]);
    const materialGroupSearchText = ref("");
    const showMaterialGroupDropdown = ref(false);
    const materialGroupPage = ref(1);
    const materialGroupPageSize = ref(50);
    const materialGroupTotal = ref(0);
    const materialGroupReqSeq = ref(0);
    let materialGroupSearchTimer = null;

    // 供应商
    const suppliers = ref([]);
    const selectedSupplierId = ref("");
    const supplierLoading = ref(false);
    const supplierSearchText = ref("");

    // 仓库
    const stores = ref([]);
    const selectedStoreId = ref("");
    const storeLoading = ref(false);

    // 物料
    const materials = ref([]);
    const selectedMaterials = ref([]);
    const materialLoading = ref(false);
    const materialSearchText = ref("");
    const materialPage = ref(1);

    // 物料编码
    const materialNoInput = ref("");
    const materialNoCandidates = ref([]);
    const selectedMaterialNo = ref("");
    const materialNoLoading = ref(false);

    const canCreate = computed(() => {
      if (createLoading.value || !selectedMaterialGroup.value) return false;
      if (currentMode.value === "field") return true;
      if (currentMode.value === "materialNo") {
        return selectedStoreId.value && materialNoInput.value.trim() && selectedMaterialNo.value;
      }
      if (currentMode.value === "normal") {
        return selectedStoreId.value && selectedSupplierId.value;
      }
      return false;
    });

    // --- API 调用 ---
    const fetchCheckHistory = async (page = 1) => {
      try {
        loading.value = true;
        errorMsg.value = "";
        current.value = page;
        const res = await stockCheckApi.getStockCheckHistory({
          current: page,
          size: size.value,
          materialGroupId: selectedMaterialGroup.value || undefined,
          status: checkStatusFilter.value,
          checkStatus: checkStatusFilter.value,
          deptName: listSearchDeptName.value || undefined,
          supplierName: listSearchSupplierName.value || undefined,
          inventeType: 1,
        });
        if (res.success) {
          checkList.value = res.data.records || [];
          total.value = res.data.total || checkList.value.length;
        } else {
          errorMsg.value = res.msg || "获取盘点历史失败";
        }
      } catch (err) {
        errorMsg.value = "获取盘点历史失败，请稍后重试";
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const fetchMaterialGroups = async (keyword = "", page = 1) => {
      const reqId = ++materialGroupReqSeq.value;
      try {
        const res = await getProjectTeams({ keyword, current: page, size: materialGroupPageSize.value });
        if (reqId !== materialGroupReqSeq.value) return;
        if (res.success && res.data) {
          const records = Array.isArray(res.data) ? res.data : res.data.records || [];
          filteredMaterialGroups.value = records.map((item) => ({
            id: item.deptId ?? item.dept_id ?? item.id,
            name: item.deptName || item.name,
          }));
          materialGroupPage.value = page;
          materialGroupTotal.value = Array.isArray(res.data) ? filteredMaterialGroups.value.length : res.data.total || filteredMaterialGroups.value.length;
        } else {
          filteredMaterialGroups.value = [];
        }
      } catch (err) {
        console.error("获取项目组列表失败", err);
        filteredMaterialGroups.value = [];
      }
    };

    const fetchSuppliersByGroupId = async (searchText = "", page = 1) => {
      try {
        supplierLoading.value = true;
        const res = await getSuppliersByGroupId({ current: page, size: 50, bizDept: selectedMaterialGroup.value, supplierName: searchText });
        suppliers.value = res.data?.records || [];
      } catch (err) {
        console.error("获取供应商失败", err);
        suppliers.value = [];
      } finally {
        supplierLoading.value = false;
      }
    };

    const fetchMaterialsBySupplierIds = async (supplierIds, searchText = "", page = 1) => {
      try {
        materialLoading.value = true;
        const res = await getMaterialsBySupplierId({ supplierId: supplierIds[0], current: page, size: 50, materialNo: searchText, materialName: searchText });
        materials.value = res.data?.records || [];
      } catch (err) {
        console.error("获取物料失败", err);
        materials.value = [];
      } finally {
        materialLoading.value = false;
      }
    };

    const fetchStoresByGroupId = async (bizDept) => {
      try {
        storeLoading.value = true;
        const res = await stockCheckApi.getWareList({ bizDept });
        stores.value = (res && res.success) ? (Array.isArray(res.data) ? res.data : []) : [];
      } catch (err) {
        console.error("获取仓库失败", err);
        stores.value = [];
      } finally {
        storeLoading.value = false;
      }
    };

    const fetchMaterialsByMaterialNoOnly = async (materialNo) => {
      try {
        materialNoLoading.value = true;
        const res = await getMaterialsBySupplierId({ bizDept: selectedMaterialGroup.value, current: 1, size: 20, materialNo });
        materialNoCandidates.value = res?.data?.records || [];
      } catch (err) {
        console.error("按物料编码获取物料失败", err);
        materialNoCandidates.value = [];
      } finally {
        materialNoLoading.value = false;
      }
    };

    // --- 搜索/筛选 ---
    const parseListSearchText = (text) => {
      const normalized = (text || "").replace(/\u3000/g, " ").replace(/\s+$/, "");
      const firstSpaceIndex = normalized.indexOf(" ");
      if (firstSpaceIndex === -1) return { deptName: normalized.trim(), supplierName: "" };
      return { deptName: normalized.slice(0, firstSpaceIndex).trim(), supplierName: normalized.slice(firstSpaceIndex).trim() };
    };

    const clearListSearch = async () => {
      listCombinedSearchText.value = "";
      listSearchDeptName.value = "";
      listSearchSupplierName.value = "";
      await fetchCheckHistory(1);
    };

    watch(listCombinedSearchText, (val) => {
      const parsed = parseListSearchText(val);
      listSearchDeptName.value = parsed.deptName;
      listSearchSupplierName.value = parsed.supplierName;
      if (listSearchTimer) clearTimeout(listSearchTimer);
      listSearchTimer = setTimeout(() => fetchCheckHistory(1), 300);
    });

    let supplierSearchTimer = null;
    const handleSupplierSearch = (query) => {
      if (supplierSearchTimer) clearTimeout(supplierSearchTimer);
      supplierSearchTimer = setTimeout(() => {
        fetchSuppliersByGroupId(query || "", 1);
      }, 300);
    };

    const handleSupplierChange = async (supplierId) => {
      selectedMaterials.value = [];
      materialSearchText.value = "";
      if (supplierId) {
        await fetchMaterialsBySupplierIds([supplierId], "", 1);
      } else {
        materials.value = [];
      }
    };

    watch(materialSearchText, () => {
      if (materialPage.value > 1) materialPage.value = 1;
      fetchMaterialsBySupplierIds(selectedSupplierId.value ? [selectedSupplierId.value] : [], materialSearchText.value.trim(), 1);
    });

    let materialNoSearchTimer = null;
    let suppressMaterialNoSearchOnce = false;
    watch(materialNoInput, () => {
      if (currentMode.value !== "materialNo") return;
      if (suppressMaterialNoSearchOnce) { suppressMaterialNoSearchOnce = false; return; }
      if (materialNoSearchTimer) clearTimeout(materialNoSearchTimer);
      materialNoSearchTimer = setTimeout(() => {
        if (currentMode.value !== "materialNo") return;
        const t = materialNoInput.value.trim();
        selectedMaterialNo.value = "";
        if (!t) { materialNoCandidates.value = []; return; }
        fetchMaterialsByMaterialNoOnly(t);
      }, 300);
    });

    watch([currentMode, selectedMaterialGroup], async ([mode, groupId]) => {
      if (mode !== "normal" || !groupId || stores.value.length > 0) return;
      await fetchStoresByGroupId(groupId);
    });

    // --- 操作 ---
    const handleFilterMaterialGroups = (query) => {
      materialGroupSearchText.value = query || "";
      if (materialGroupSearchTimer) clearTimeout(materialGroupSearchTimer);
      materialGroupSearchTimer = setTimeout(() => fetchMaterialGroups(query || "", 1), 300);
    };

    const handleSelectGroup = async (groupId) => {
      const group = filteredMaterialGroups.value.find((g) => g.id === groupId);
      selectedMaterialGroup.value = groupId;
      selectedMaterialGroupName.value = group?.name || "";
      selectedStoreId.value = "";
      stores.value = [];
      selectedSupplierId.value = "";
      supplierSearchText.value = "";
      selectedMaterials.value = [];
      materialSearchText.value = "";
      materials.value = [];
      materialNoInput.value = "";
      materialNoCandidates.value = [];
      selectedMaterialNo.value = "";
      if (groupId) {
        await fetchStoresByGroupId(groupId);
        await fetchSuppliersByGroupId("", 1);
      }
    };

    const toggleMaterialSelection = (materialId) => {
      const idx = selectedMaterials.value.indexOf(materialId);
      if (idx > -1) selectedMaterials.value.splice(idx, 1);
      else selectedMaterials.value.push(materialId);
    };

    const selectMaterialNo = (materialNo) => {
      suppressMaterialNoSearchOnce = true;
      selectedMaterialNo.value = materialNo;
      materialNoInput.value = materialNo;
    };

    const setStatus = (message, type = "success") => {
      statusMsg.value = message;
      statusType.value = type;
      setTimeout(() => { if (statusMsg.value === message) statusMsg.value = ""; }, 3000);
    };

    const createCheckOrder = async () => {
      if (!selectedMaterialGroup.value) { setStatus("请选择项目组", "error"); return; }
      if (currentMode.value === "materialNo" && !selectedStoreId.value) { setStatus("请选择仓库", "error"); return; }
      if (currentMode.value === "materialNo" && !materialNoInput.value.trim()) { setStatus("请输入物料编码", "error"); return; }
      if (currentMode.value === "materialNo" && !selectedMaterialNo.value) { setStatus("请从列表选择一个物料编码", "error"); return; }
      if (currentMode.value === "normal" && !selectedSupplierId.value) { setStatus("请选择供应商", "error"); return; }
      if (currentMode.value === "normal" && !selectedStoreId.value) { setStatus("请选择仓库", "error"); return; }

      const materialStatus = selectedMaterials.value.length > 0 ? `已选择 ${selectedMaterials.value.length} 个物料` : "未选择物料（将包含所有物料）";
      const materialNoForConfirm = selectedMaterialNo.value || materialNoInput.value.trim();
      const materialNoStatus = currentMode.value === "materialNo" ? `物料编码：${materialNoForConfirm}` : "";

      if (!confirm(`是否创建盘点单？\n${materialNoStatus || materialStatus}\n确定继续吗？`)) return;

      try {
        createLoading.value = true;
        setStatus("创建中...", "info");
        let res;
        if (currentMode.value === "field") {
          res = await stockCheckApi.stockcheckAddByBizDept({ bizDept: selectedMaterialGroup.value });
        } else if (currentMode.value === "materialNo") {
          res = await stockCheckApi.materialNoSave({ materialGroupId: selectedMaterialGroup.value, bizDept: selectedMaterialGroup.value, biz_dept: selectedMaterialGroup.value, storeId: selectedStoreId.value, materialNo: selectedMaterialNo.value });
        } else {
          res = await stockCheckApi.addStockCheck({
            materialGroupId: selectedMaterialGroup.value, bizDept: selectedMaterialGroup.value, biz_dept: selectedMaterialGroup.value, mode: currentMode.value, storeId: selectedStoreId.value, supplierIds: selectedSupplierId.value,
            materialIds: selectedMaterials.value.length > 0 ? selectedMaterials.value.join(",") : "",
          });
        }
        if (res.success) {
          setStatus("创建盘点单成功", "success");
          resetModal();
          fetchCheckHistory();
        } else {
          setStatus(`创建失败：${res.msg || "未知错误"}`, "error");
        }
      } catch (err) {
        console.error("创建盘点单失败", err);
        setStatus(`创建盘点单失败：${err?.message || "请稍后重试"}`, "error");
      } finally {
        createLoading.value = false;
      }
    };

    const resetModal = () => {
      showAddModal.value = false;
      selectedMaterialGroup.value = "";
      selectedMaterialGroupName.value = "";
      materialGroupSearchText.value = "";
      filteredMaterialGroups.value = [];
      selectedStoreId.value = "";
      stores.value = [];
      selectedSupplierId.value = "";
      supplierSearchText.value = "";
      suppliers.value = [];
      selectedMaterials.value = [];
      materialSearchText.value = "";
      materials.value = [];
      materialNoInput.value = "";
      materialNoCandidates.value = [];
      selectedMaterialNo.value = "";
      currentMode.value = "field";
    };

    const handleDeleteCheck = async (id) => {
      if (!id || deletingId.value) return;
      if (!confirm("确认删除该盘点单吗？")) return;
      try {
        deletingId.value = String(id);
        const res = await stockCheckApi.deleteStockCheck(id);
        if (res && res.success) {
          setStatus("删除成功", "success");
          await fetchCheckHistory(current.value);
        } else {
          setStatus(`删除失败：${res?.msg || "未知错误"}`, "error");
        }
      } catch (err) {
        console.error("删除盘点单失败", err);
        setStatus("删除盘点单失败，请稍后重试", "error");
      } finally {
        deletingId.value = "";
      }
    };

    const handleEnterCheck = (id, bizDept) => {
      router.push({ path: `/manual-stock-check/detail/${id}`, query: { fromProjectTeam: true, bizDept: bizDept || "" } });
    };

    const navigateToDetail = (id, bizDept) => {
      router.push({ path: `/manual-stock-check/detail/${id}`, query: { fromProjectTeam: true, bizDept: bizDept || "" } });
    };

    const handleBack = () => router.push("/home");

    const handleAddCheck = async () => {
      showAddModal.value = true;
      await fetchMaterialGroups("", 1);
    };

    const handlePageChange = async (page) => {
      if (page < 1 || page > totalPages.value || loading.value) return;
      await fetchCheckHistory(page);
    };

    const onFilterChange = () => fetchCheckHistory(1);

    const getTagType = (status) => {
      const map = { 0: "info", 1: "success", 2: "danger", 3: "warning" };
      return map[status] || "info";
    };

    const getStatusText = (status) => {
      const map = { 0: "待盘点", 1: "通过", 2: "不通过", 3: "已取消" };
      return map[status] || "未知状态";
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    };

    const openSupplierNameModal = (item) => {
      const raw = (item?.supplierName || "").trim();
      if (!raw) return;
      supplierNameModalTitle.value = item?.inventeNo ? `盘点单 ${item.inventeNo} 供应商` : "供应商";
      supplierNameModalItems.value = raw.split(/[，,]/).map((s) => (s || "").trim()).filter(Boolean);
      showSupplierNameModal.value = true;
    };

    onMounted(() => fetchCheckHistory(1));
    onUnmounted(() => { if (listSearchTimer) clearTimeout(listSearchTimer); if (supplierSearchTimer) clearTimeout(supplierSearchTimer); });

    return {
      checkList, loading, total, current, size, totalPages, checkStatusFilter, statusOptions, onFilterChange,
      listCombinedSearchText, clearListSearch,
      showSupplierNameModal, supplierNameModalTitle, supplierNameModalItems, openSupplierNameModal,
      errorMsg, statusMsg, statusType, showAddModal, currentMode, createLoading, canCreate,
      handlePageChange,
      selectedMaterialGroup, filteredMaterialGroups, materialGroupSearchText, showMaterialGroupDropdown,
      selectedMaterialGroupName, handleFilterMaterialGroups, handleSelectGroup,
      suppliers, supplierLoading, selectedSupplierId, handleSupplierSearch, handleSupplierChange,
      stores, storeLoading, selectedStoreId,
      materials, materialLoading, selectedMaterials, materialSearchText, toggleMaterialSelection,
      materialNoInput, materialNoCandidates, selectedMaterialNo, materialNoLoading, selectMaterialNo,
      createCheckOrder, resetModal, deletingId, handleDeleteCheck, handleEnterCheck, navigateToDetail,
      handleBack, handleAddCheck, getTagType, getStatusText, formatDate,
    };
  },
};
</script>

<style scoped>
.stock-check-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--background-color, #f5f5f5);
  font-size: 15px;
}

.stock-check-page :deep(.el-button) {
  font-size: 15px;
}

.stock-check-page :deep(.el-input__inner),
.stock-check-page :deep(.el-input__wrapper) {
  font-size: 15px;
}

.stock-check-page :deep(.el-tag) {
  font-size: 13px;
}

.stock-check-page :deep(.el-radio-button__inner),
.stock-check-page :deep(.el-radio__label),
.stock-check-page :deep(.el-select .el-select__placeholder),
.stock-check-page :deep(.el-select .el-select__selected-item),
.stock-check-page :deep(.el-dialog__title),
.stock-check-page :deep(.el-form-item__label),
.stock-check-page :deep(.el-empty__description) {
  font-size: 15px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--header-background, #fff);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 50px;
  box-sizing: border-box;
}

.header .back-btn {
  background: none;
  border: none;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.header .icon-back {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.header .title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
  color: var(--header-text, #333);
}

.content {
  flex: 1;
  padding: 8px 10px;
  padding-top: 58px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.filter-bar {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 8px;
}

.filter-bar > .el-button + .el-button {
  margin-left: 6px;
}

.filter-bar .el-button {
  flex: 1;
}

.search-bar {
  margin-bottom: 8px;
}

.status-alert {
  margin-bottom: 8px;
}

.loading-area {
  min-height: 120px;
}

/* 卡片列表 */
.card-list {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  flex-direction: column;
}

.card-list > .stock-card + .stock-card {
  margin-top: 8px;
}

.stock-card {
  background: var(--card-background, #fff);
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border-left: 3px solid var(--el-color-primary);
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.card-no {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary, #333);
}

.card-info {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  flex-direction: column;
}

.card-info > .info-row + .info-row {
  margin-top: 3px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  line-height: 1.5;
}

.info-label {
  color: var(--text-secondary, #999);
  flex-shrink: 0;
}

.info-value {
  color: var(--text-primary, #333);
  text-align: right;
  margin-left: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supplier-link { cursor: default; }
.supplier-link.clickable { cursor: pointer; color: var(--el-color-primary); }
.text-danger { color: var(--el-color-danger); }

.card-actions {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--el-border-color-lighter, #ebeef5);
}

.card-actions > .el-button + .el-button {
  margin-left: 8px;
}

.card-actions .el-button { flex: 1; }

/* 分页 */
.pagination-wrap {
  display: flex;
  justify-content: center;
  padding: 10px 0 20px;
}

/* 弹窗内 */
.mode-switch {
  width: 100%;
  margin-bottom: 12px;
}

.mode-switch :deep(.el-radio-button) { flex: 1; }
.mode-switch :deep(.el-radio-button__inner) { width: 100%; }

.form-item {
  margin-bottom: 12px;
}

.form-item :deep(.el-form-item__label) {
  font-size: 15px;
  font-weight: 500;
}

.store-radio-group {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-wrap: wrap;
  flex-wrap: wrap;
}

.store-radio {
  margin-right: 6px !important;
}

/* 可选列表 */
.selectable-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--el-border-color-lighter, #ebeef5);
  border-radius: 6px;
  margin-bottom: 8px;
}

.selectable-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid var(--el-border-color-lighter, #ebeef5);
  transition: background 0.15s;
}

.selectable-item:last-child { border-bottom: none; }
.selectable-item:hover { background: rgba(0, 0, 0, 0.02); }
.selectable-item.active { background: rgba(64, 158, 255, 0.08); }

.check-mark {
  color: var(--el-color-primary);
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
}

.item-main {
  flex: 1;
  min-width: 0;
}

.item-no {
  display: block;
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary, #333);
}

.item-sub {
  display: block;
  font-size: 14px;
  color: var(--text-secondary, #999);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.form-hint {
  margin: 0 0 8px;
  font-size: 14px;
  color: var(--text-secondary, #999);
}

/* 供应商弹窗 */
.supplier-list-popup {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-wrap: wrap;
  flex-wrap: wrap;
}

.supplier-tag {
  font-size: 15px;
  margin: 0 6px 6px 0;
}

@media (max-width: 768px) {
  .header { padding: 8px 10px; height: 46px; }
  .header .title { font-size: 18px; }
  .content { padding: 6px 8px; padding-top: 54px; }
  .stock-card { padding: 10px 12px; }
  .selectable-item { padding: 10px; }
}
</style>
